import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import dayjs from 'dayjs'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import React from 'react'

import { TextField, Select } from 'app/components/generic/FormElements'
import { validateNotEmpty } from 'app/validators'

// Mirrors MAX_WALLET_VALIDITY_EXTENSION_MONTHS in oh-api (oh_memberships constants).
const MAX_EXTENSION_MONTHS = 12
const MONTH_OPTIONS = Array.from(
  { length: MAX_EXTENSION_MONTHS },
  (_, index) => index + 1
)
const DATE_FORMAT = 'D MMM, YYYY'

/**
 * Super-admin only: push a parent's wallet validity out by 1-12 months.
 *
 * The preview mirrors the backend rule — N months after the LATER of the
 * current validity and today, snapped to the end of that month. Only the date
 * moves: if the wallet already lapsed and the expiry cron zeroed it, the
 * expired coins stay expired.
 */
const ExtendWalletValidityForm = (props) => {
  const { coinSummary } = props
  const { errors, values, setFieldValue } = useFormikContext()

  const expiresAt = coinSummary?.expires_at
    ? dayjs(coinSummary.expires_at)
    : null
  const isExpired = expiresAt ? expiresAt.isBefore(dayjs()) : false

  React.useEffect(() => {
    setFieldValue(
      'current_validity',
      expiresAt ? expiresAt.format(DATE_FORMAT) : 'Not set'
    )
  }, [coinSummary?.expires_at])

  React.useEffect(() => {
    const months = parseInt(values?.months)
    if (!months) {
      setFieldValue('new_validity', '')
      return
    }
    const base = expiresAt && !isExpired ? expiresAt : dayjs()
    setFieldValue(
      'new_validity',
      base.add(months, 'month').endOf('month').format(DATE_FORMAT)
    )
  }, [values?.months, coinSummary?.expires_at])

  return (
    <>
      <FormControl variant="outlined">
        <TextField
          name={'current_validity'}
          label={'Current Validity'}
          variant="outlined"
          disabled
        />
        {isExpired ? (
          <FormHelperText>
            This wallet expired on {expiresAt.format(DATE_FORMAT)}, so the
            extension counts from today. Coins that already expired are not
            restored.
          </FormHelperText>
        ) : null}
      </FormControl>
      <FormControl
        variant="outlined"
        {...(!isEmpty(errors.months) ? { error: true } : null)}
        required
      >
        <InputLabel>{'Extend by (months)'}</InputLabel>
        <Select
          name={'months'}
          defaultValue={''}
          label={'Extend by (months)'}
          validate={validateNotEmpty({ isRequired: true })}
        >
          {MONTH_OPTIONS.map((months) => (
            <MenuItem key={months} value={months}>
              {months} {months === 1 ? 'month' : 'months'}
            </MenuItem>
          ))}
        </Select>
        {!isEmpty(errors.months) && (
          <FormHelperText>{errors.months}</FormHelperText>
        )}
      </FormControl>
      <FormControl variant="outlined">
        <TextField
          name={'new_validity'}
          label={'New Validity'}
          variant="outlined"
          disabled
        />
      </FormControl>
      <FormControl
        variant="outlined"
        {...(!isEmpty(errors.notes) ? { error: true } : null)}
        required
      >
        <TextField
          name={'notes'}
          label={'Notes'}
          variant="outlined"
          required
          validate={validateNotEmpty({ isRequired: true })}
        />
        {!isEmpty(errors.notes) && (
          <FormHelperText>{errors.notes}</FormHelperText>
        )}
      </FormControl>
    </>
  )
}

export default ExtendWalletValidityForm
